import * as React from 'react';

import { SlideOut } from '@components/common';

import { QuestionConfigContainer } from '../QuestionConfig/QuestionConfigContainer';
import { QuestionConfigSlideoutFooter } from '../QuestionConfig/QuestionConfigSlideoutFooter';
import { QuestionRulesProvider, useQuestionRulesContext } from '../QuestionConfig/QuestionRulesContext';

import { ScreenerConfigContainer } from './ScreenerConfigContainer';
import { ScreenerConfigSlideoutFooter } from './ScreenerConfigSlideoutFooter';
import { useScreenerConfig } from './useScreenerConfig';
import { TabType } from './utils';

interface Props {
  study: Study;
  open: boolean;
  screener: Screener;
  onClose: () => void;
  setQuestions: (questions: ScreenerField[]) => void;
}
type ViewType = 'tabs' | 'question_config';

const QuestionConfigFooterWrapper: React.FC = () => {
  const questionRulesData = useQuestionRulesContext();

  return (
    <QuestionConfigSlideoutFooter
      isLoading={questionRulesData.isLoading}
      groupedRules={questionRulesData.groupedRules}
      onSave={questionRulesData.handleSave}
      onCancel={questionRulesData.onClose}
      onRemoveAll={questionRulesData.onRemoveAll}
    />
  );
};

export const ScreenerConfigSlideout: React.FC<Props> = ({ study, screener, onClose, open, setQuestions }) => {
  const {
    isLoading,
    handleSave,
    setDisqualificationScreenerReview,
    disqualificationScreenerReview,
    qualificationScreenerReview,
    setQualificationScreenerReview,
    disqualificationPageTitle,
    setDisqualificationPageTitle,
    disqualificationPageContent,
    setDisqualificationPageContent,
    disqualificationPageTitleError,
    disqualificationPageContentError
  } = useScreenerConfig(study, screener);
  const [currentView, setCurrentView] = React.useState<ViewType>('tabs');

  const [selectedQuestion, setSelectedQuestion] = React.useState<ScreenerField | null>(null);
  const [activeTab, setActiveTab] = React.useState<TabType>('preferences');

  const handleEditRule = (question: ScreenerField) => {
    setSelectedQuestion(question);
    setCurrentView('question_config');
  };

  const handleBackToTabs = () => {
    setCurrentView('tabs');
    setSelectedQuestion(null);
  };

  const handleClose = () => {
    setCurrentView('tabs');
    setSelectedQuestion(null);
    onClose();
  };

  const handleSaveQualificationPreferences = async () => {
    const success = await handleSave();
    if (success) {
      handleClose();
    }
  };

  if (!open) {
    return null;
  }

  if (currentView === 'question_config' && selectedQuestion) {
    return (
      <QuestionRulesProvider
        question={selectedQuestion}
        screener={screener}
        onClose={handleClose}
        setQuestions={setQuestions}
      >
        <SlideOut
          title={'Review logic'}
          onClose={handleClose}
          renderFooter={() => <QuestionConfigFooterWrapper />}
          onBack={handleBackToTabs}
          noHeader={true}
          size='xl'
        >
          <div className='pt-6'>
            <QuestionConfigContainer />
          </div>
        </SlideOut>
      </QuestionRulesProvider>
    );
  }

  return (
    <SlideOut
      title={'Review logic'}
      onClose={onClose}
      renderFooter={() => (
        <ScreenerConfigSlideoutFooter
          isLoading={isLoading}
          onSave={handleSaveQualificationPreferences}
          onCancel={handleClose}
        />
      )}
      size='xl'
    >
      <ScreenerConfigContainer
        qualificationReview={qualificationScreenerReview}
        disqualificationReview={disqualificationScreenerReview}
        setQualificationReview={setQualificationScreenerReview}
        setDisqualificationReview={setDisqualificationScreenerReview}
        screener={screener}
        onClose={onClose}
        setQuestions={setQuestions}
        handleEditRule={handleEditRule}
        setActiveTab={setActiveTab}
        activeTab={activeTab}
        disqualificationPageTitle={disqualificationPageTitle}
        setDisqualificationPageTitle={setDisqualificationPageTitle}
        disqualificationPageContent={disqualificationPageContent}
        setDisqualificationPageContent={setDisqualificationPageContent}
        disqualificationPageTitleError={disqualificationPageTitleError}
        disqualificationPageContentError={disqualificationPageContentError}
      />
    </SlideOut>
  );
};
